/**
 * S13 (Pedro 2026-10-11): read a client record the client filled in on a computer and
 * choose what to take from it. "In essence, nothing changes silently."
 *
 * 1. The user picks the filled PDF; the server reads its boxes (`readFilledClientRecord`;
 *    nothing about the person changes there).
 * 2. A review table: one line per value read, "Now" (what the page holds) beside "From the
 *    file", with a tick. Values that fill an EMPTY field come ticked; a value that DIFFERS
 *    from one already there needs an explicit tick; an identical one is shown as "Same".
 *    Boxes that could not be read are listed with their text, to be entered by hand.
 * 3. "Use the ticked values" hands them to the page, which stages them in its Edit
 *    transaction (orange, saved only on Save; an RM's Save is filed for approval) and
 *    offers a toggle between the values before and after the upload until Save.
 *    Optionally, the file itself is staged as a person document, "Client record (filled
 *    form)" (unticked by default: a filled form is not the signed copy).
 *
 * Only a form downloaded from the app (Blank legal templates) and filled on a computer
 * can be read; the server's message says so for anything else.
 */
import {
  Alert, Badge, Button, Checkbox, FileInput, Group, Modal, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconFileImport } from '@tabler/icons-react';
import { useState } from 'react';

import {
  readFilledClientRecord, type ClientRecordReadResult,
} from '../api/clientRecord';
import type { UUID } from '../api/types';
import { PEP_OPTIONS } from '../utils/pepLabels';
import { CLIENT_RECORD_BLOCKS, describeClientBlock, isBlank } from './ClientRecordFields';

/** The Person page's form shape for a value from the API (yes/no as 'Yes' / 'No', whole
 *  numbers as text, everything else as is). */
export function apiToFormValue(field: string, v: unknown): unknown {
  if (field === 'us_person' || field === 'tax_obligations_met' || field === 'listed_company') {
    return v === true ? 'Yes' : v === false ? 'No' : '';
  }
  if (field === 'children_count' || field === 'mobile_country_code' || field === 'fixed_phone_country_code') {
    return v == null ? '' : String(v);
  }
  return v ?? '';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A form value in words, as one or more lines ("—" = not answered). */
export function showFormValue(field: string, v: unknown): string[] {
  if (isBlank(v)) return ['—'];
  if (CLIENT_RECORD_BLOCKS.has(field)) return describeClientBlock(field, v);
  if (typeof v === 'boolean') return [v ? 'Yes' : 'No'];
  const s = String(v);
  if (field === 'pep_status') return [PEP_OPTIONS.find((o) => o.value === s)?.label ?? s];
  if (ISO_DATE.test(s)) {
    const [y, m, d] = s.split('-');
    return [`${d}/${m}/${y}`];
  }
  return [s];
}

/** Same meaning on screen (blocks compared line by line, blank lines ignored). */
export function sameFormValue(field: string, a: unknown, b: unknown): boolean {
  if (isBlank(a) && isBlank(b)) return true;
  return JSON.stringify(showFormValue(field, a)) === JSON.stringify(showFormValue(field, b));
}

interface Line { field: string; now: unknown; read: unknown; same: boolean }

export function ClientRecordImportModal({
  opened, onClose, personId, current, label, onUse,
}: {
  opened: boolean;
  onClose: () => void;
  personId: UUID;
  /** The page's form values now. */
  current: Record<string, unknown>;
  /** The page's friendly name for a field. */
  label: (field: string) => string;
  /** The ticked values (form shape) and, if asked, the file to keep. */
  onUse: (values: Record<string, unknown>, keepFile: File | null, fileName: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClientRecordReadResult | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [keep, setKeep] = useState(false);

  function reset() {
    setFile(null); setBusy(false); setError(null); setResult(null);
    setLines([]); setTicked(new Set()); setKeep(false);
  }
  function close() { reset(); onClose(); }

  async function read(f: File | null) {
    setFile(f); setError(null); setResult(null); setLines([]); setTicked(new Set());
    if (!f) return;
    setBusy(true);
    try {
      const r = await readFilledClientRecord(personId, f);
      const ls = Object.entries(r.values).map(([field, v]) => {
        const read = apiToFormValue(field, v);
        const now = current[field];
        return { field, now, read, same: sameFormValue(field, now, read) };
      });
      setResult(r);
      setLines(ls);
      // An empty field takes the file's value by default; a different value needs a tick.
      setTicked(new Set(ls.filter((l) => !l.same && isBlank(l.now)).map((l) => l.field)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The client record could not be read.');
    } finally {
      setBusy(false);
    }
  }

  function toggle(field: string, on: boolean) {
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(field); else next.delete(field);
      return next;
    });
  }

  function use() {
    if (!file) return;
    const values = Object.fromEntries(lines.filter((l) => ticked.has(l.field)).map((l) => [l.field, l.read]));
    onUse(values, keep ? file : null, file.name);
    close();
  }

  const changing = lines.filter((l) => !l.same);
  // Select all (Pedro's smoke, 2026-10-11): every tickable line, i.e. all but "Same".
  const allTicked = changing.length > 0 && changing.every((l) => ticked.has(l.field));
  const someTicked = changing.some((l) => ticked.has(l.field));
  function toggleAll() {
    setTicked(allTicked ? new Set() : new Set(changing.map((l) => l.field)));
  }
  const cell = (field: string, v: unknown) => (
    <Stack gap={0}>{showFormValue(field, v).map((t, i) => <Text key={i} size="sm">{t}</Text>)}</Stack>
  );

  return (
    <Modal opened={opened} onClose={close} centered size="xl"
      title={<Group gap="xs"><IconFileImport size={18} /><Text fw={600}>Import from a filled client record</Text></Group>}>
      <Stack>
        <Text size="sm" c="dimmed">
          Pick the client record the client filled in on a computer (downloaded from Blank legal
          templates). Nothing changes until you tick values, use them, and press Save.
        </Text>
        <FileInput label="Filled client record (PDF)" placeholder="Choose a PDF" accept="application/pdf"
          value={file} onChange={(f) => void read(f)} clearable disabled={busy} />
        {busy && <Text size="sm" c="dimmed">Reading the file…</Text>}
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not read this file">{error}</Alert>}

        {result && (
          <>
            {lines.length === 0 ? (
              <Alert color="blue">No values were filled in on this form.</Alert>
            ) : (
              <>
                <Text size="sm">
                  {lines.length} value{lines.length === 1 ? '' : 's'} read · {changing.length} different
                  from the page. Values for empty fields come ticked; tick a different value to replace
                  the one there.
                </Text>
                <Table withTableBorder striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={36}>
                        {changing.length > 0 && (
                          <Tooltip label="Tick all values that differ from the page" withArrow>
                            <Checkbox aria-label="Tick all values that differ from the page"
                              checked={allTicked} indeterminate={someTicked && !allTicked}
                              onChange={toggleAll} />
                          </Tooltip>
                        )}
                      </Table.Th>
                      <Table.Th>Field</Table.Th>
                      <Table.Th>Now</Table.Th>
                      <Table.Th>From the file</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {lines.map((l) => (
                      <Table.Tr key={l.field}>
                        <Table.Td>
                          {l.same ? null : (
                            <Checkbox aria-label={`Use ${label(l.field)}`} checked={ticked.has(l.field)}
                              onChange={(e) => toggle(l.field, e.currentTarget.checked)} />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{label(l.field)}</Text>
                          {l.same
                            ? <Badge size="xs" variant="light" color="gray">Same</Badge>
                            : !isBlank(l.now) && <Badge size="xs" variant="light" color="orange">Different</Badge>}
                        </Table.Td>
                        <Table.Td c={l.same ? 'dimmed' : undefined}>{cell(l.field, l.now)}</Table.Td>
                        <Table.Td c={l.same ? 'dimmed' : undefined}>{cell(l.field, l.read)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
            {result.unread.length > 0 && (
              <Alert color="orange" icon={<IconAlertCircle size={16} />}
                title={`${result.unread.length} box${result.unread.length === 1 ? '' : 'es'} could not be read — enter ${result.unread.length === 1 ? 'it' : 'them'} by hand if needed`}>
                <Stack gap={2}>
                  {result.unread.map((u) => (
                    <Text key={u.field} size="sm">
                      {u.label === u.field ? label(u.field) : u.label}
                      {u.text ? `: "${u.text}"` : ''} — {u.reason}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}
            <Checkbox checked={keep} onChange={(e) => setKeep(e.currentTarget.checked)}
              label='Also keep this file on the person, as "Client record (filled form)" (saved with the page)' />
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button onClick={use} disabled={!result || (ticked.size === 0 && !keep)}>
            {ticked.size > 0 ? `Use ${ticked.size} ticked value${ticked.size === 1 ? '' : 's'}` : 'Keep the file only'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
